// src/components/desk/contract/ClauseEditor.jsx — ONE section's clause text, opened on demand
// (designer 2a "clause text — one language at a time", 5a "side by side on desktop").
//
// Rules that look like styling and are not:
//   • Clause text is COLLAPSED by default (round 1 decision 1). This editor is mounted for the
//     one section the agent tapped — never one editor per section down the page.
//   • On a phone it is a full-screen SHEET: language pills at the top, ONE language's text at
//     a time. On a desktop (`wide`) it sits inline under the section's row with the contract's
//     languages SIDE BY SIDE, so an edit in one column shows at once whether the other still
//     matches (designer: "an edit in one column shows immediately whether the other still
//     matches").
//   • The text is shown READ-ONLY first, with the `{{…}}` markers as green chips (the values
//     the form fills in); "edit" reveals the textarea. The hint says the markers are values
//     and must not be typed over. "↺ original" restores the template text of THAT language.
//   • A `translation_drift` issue naming one of this section's clauses is drawn INLINE, on
//     the language it names ("Clause 3b · {{prepay_summary}} missing in English") — beside
//     the text that can fix it, not in a list somewhere else.
//   • A custom section has one clause and no default; its × removes the whole section.
//   • `readOnly` (after a signature): the text is drawn, nothing edits.

import { useState } from 'react'
import { t, tLog, tOr } from '../../../lib/i18n.js'
import { Icon } from '../../Icon.jsx'
import { clauseEdited, conditionLabel, sectionIssues, sectionNumbers, sectionTitle, splitMarkers } from '../ContractSections.jsx'
import { BTN_ON_PAPER, INK_MUTED, L, MARKER, PINP } from './paper.js'

// "Clause 3b · {{prepay_summary}} missing in English" / "Clause 4 · text missing in Russian".
export function driftIssueText(it) {
  const lang = tOr(`desk_contract_lang_${it.lang}`, it.lang || '')
  return it.kind === 'placeholders'
    ? tLog('desk_contract_drift_ph_named', { clause_no: it.clause_no ?? '', detail: it.detail || '{{…}}', lang })
    : tLog('desk_contract_drift_text_named', { clause_no: it.clause_no ?? '', lang })
}

// The read-only rendering: prose as-is, markers as chips titled with the field's label.
export function ClauseText({ text, fields }) {
  const parts = splitMarkers(text)
  if (parts.length === 0) return <span className={`italic ${INK_MUTED}`}>{t('desk_contract_clause_empty')}</span>
  return (
    <span className="whitespace-pre-wrap leading-relaxed">
      {parts.map((p, i) => typeof p === 'string' ? <span key={i}>{p}</span> : (
        <span key={i} className={MARKER} title={L((fields || []).find(f => f.key === p.marker), 'label') || p.marker}>{p.raw}</span>
      ))}
    </span>
  )
}

// One language's block: read-only text, then the textarea once "edit" is pressed (or at once
// when the text is empty — an empty custom section has nothing to read).
function LangBlock({ lang, clause, fields, issues, readOnly, disabled, onText, onReset, custom }) {
  const text = clause[`text_${lang}`] ?? ''
  const edited = clauseEdited(clause, lang)
  const [editing, setEditing] = useState(!readOnly && !text.trim())
  const mine = (issues || []).filter(it => it.lang === lang && String(it.clause_no) === String(clause.clause_no))
  return (
    <div className="space-y-1.5" data-lang={lang} data-clause-no={clause.clause_no}>
      {editing && !readOnly ? (
        <textarea className={`${PINP} w-full min-h-[7rem] font-normal ${edited ? 'border-[#1F6F4F]/50' : ''} ${mine.length ? 'border-red-700/60' : ''}`}
          value={text} disabled={disabled} data-lang={lang}
          onChange={e => onText(e.target.value)} />
      ) : (
        <div className={`rounded-md border px-2.5 py-2 text-[13px] ${mine.length ? 'border-red-700/50 bg-red-700/5' : 'border-[#14130F]/10 bg-white/60'}`}>
          <ClauseText text={text} fields={fields} />
        </div>
      )}
      {mine.map((it, i) => (
        <div key={i} className="text-[11px] text-red-700 flex items-start gap-1" data-testid="contract-drift-inline">
          <span aria-hidden="true">!</span><span>{driftIssueText(it)}</span>
        </div>
      ))}
      {!readOnly && (
        <div className="flex items-center gap-3 text-[11px]">
          {!editing && (
            <button type="button" className="flex items-center gap-1 text-[#14130F]/70 hover:text-[#14130F] underline decoration-dotted" disabled={disabled} onClick={() => setEditing(true)}>
              <Icon name="pencil" size={11} /> {t('desk_contract_clause_edit')}
            </button>
          )}
          {edited && !custom && (<>
            <span className="text-[#1F6F4F]">{t('desk_contract_clause_edited')}</span>
            <button type="button" className={`underline decoration-dotted ${INK_MUTED} hover:text-[#14130F]`} disabled={disabled} onClick={() => { onReset(); }}>
              {t('desk_contract_clause_original')}
            </button>
          </>)}
        </div>
      )}
    </div>
  )
}

export function ClauseEditor({ section: s, sections, langs, fields, issues, wide, readOnly = false, disabled = false, onChange, onClose, onRemove }) {
  const chosen = langs && langs.length ? langs : ['ka']
  const [lang, setLang] = useState(chosen[0])
  const cur = chosen.includes(lang) ? lang : chosen[0]
  const numbers = sectionNumbers(sections)
  const n = numbers.get(s.key)
  const mine = sectionIssues(s, issues)
  const setClause = (clauseNo, patch) => onChange(sections.map(x => x.key !== s.key ? x
    : { ...x, clauses: x.clauses.map(c => c.clause_no === clauseNo ? { ...c, ...patch } : c) }))

  const head = (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-mono tabular-nums text-[11px] text-[#14130F]/50 shrink-0">{n ?? '·'}</span>
      <span className="font-medium text-[14px] truncate">{sectionTitle(s)}</span>
      {s.custom && <span className="shrink-0 font-mono text-[9px] tracking-[.12em] uppercase rounded border border-[#14130F]/25 px-1 text-[#14130F]/60">{t('desk_contract_section_custom')}</span>}
      {s.custom && !readOnly && onRemove && (
        <button type="button" className="shrink-0 text-[#14130F]/50 hover:text-red-700 p-1" title={t('desk_contract_section_remove')} disabled={disabled} onClick={() => onRemove(s.key)}>
          <Icon name="x" size={12} />
        </button>
      )}
    </div>
  )
  const hint = !readOnly && <div className={`text-[11px] ${INK_MUTED} flex items-center gap-1.5`}><span className={MARKER}>{'{{…}}'}</span> {t('desk_contract_marker_hint')}</div>
  const langPills = !wide && chosen.length > 1 && (
    <div className="flex items-center gap-1.5" data-testid="contract-clause-langs">
      {chosen.map(l => (
        <button key={l} type="button" data-lang={l} aria-pressed={cur === l}
          className={`rounded-full px-3 py-1 text-[12px] font-medium border ${cur === l ? 'bg-[#14130F] text-[#F7F5F0] border-[#14130F]' : 'border-[#14130F]/25 text-[#14130F]/70'}`}
          onClick={() => setLang(l)}>
          {tOr(`desk_contract_lang_short_${l}`, l)}
          {mine.some(it => it.lang === l) && <span className="ml-1 text-red-400">!</span>}
        </button>
      ))}
    </div>
  )
  const cols = wide ? chosen : [cur]
  const body = (
    <div className="space-y-4">
      {s.clauses.map(c => (
        <div key={c.clause_no} className="space-y-2" data-clause={c.clause_no}>
          {(s.clauses.length > 1 || c.condition) && (
            <div className={`text-[11px] ${INK_MUTED} flex items-center gap-2 flex-wrap`}>
              {s.clauses.length > 1 && <span className="font-mono">{c.clause_no}</span>}
              {c.condition && <span className="italic">{t('desk_contract_clause_shown_when')}: {conditionLabel(c.condition, fields)}</span>}
            </div>
          )}
          <div className={wide && cols.length > 1 ? 'grid gap-3' : ''} style={wide && cols.length > 1 ? { gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` } : undefined}>
            {cols.map(l => (
              <div key={l} className="min-w-0 space-y-1">
                {wide && cols.length > 1 && <div className="font-mono text-[10px] tracking-[.12em] uppercase text-[#14130F]/50">{tOr(`desk_contract_lang_${l}`, l)}</div>}
                <LangBlock key={`${c.clause_no}-${l}`} lang={l} clause={c} fields={fields} issues={mine} readOnly={readOnly} disabled={disabled}
                  custom={!!s.custom}
                  onText={v => setClause(c.clause_no, { [`text_${l}`]: v })}
                  onReset={() => setClause(c.clause_no, { [`text_${l}`]: c[`default_${l}`] ?? '' })} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  if (wide) {
    return (
      <div className="rounded-xl border border-[#14130F]/12 bg-[#F7F5F0] p-3 space-y-3 scroll-mt-3" data-testid="contract-clause-editor" data-section-key={s.key} data-layout="inline">
        <div className="flex items-center gap-2">
          {head}
          <button type="button" className="ml-auto shrink-0 text-[12px] text-[#14130F]/70 hover:text-[#14130F] flex items-center gap-1" onClick={onClose}>
            <Icon name="chevron-down" size={12} /> {t('desk_contract_collapse')}
          </button>
        </div>
        {hint}
        {body}
      </div>
    )
  }
  // The phone: a full-screen sheet over the page.
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#F7F5F0] text-[#14130F]" data-testid="contract-clause-editor" data-section-key={s.key} data-layout="sheet">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#14130F]/10 bg-[#F7F5F0]">
        <button type="button" className="shrink-0 px-1 -ml-1 text-[#14130F]/70 text-base leading-none" aria-label={t('desk_contract_back')} onClick={onClose}>←</button>
        {head}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {langPills}
        {hint}
        {body}
      </div>
      <div className="px-3 py-2 border-t border-[#14130F]/10 flex items-center justify-end gap-2 bg-[#F7F5F0]">
        <button type="button" className={BTN_ON_PAPER} onClick={onClose}>{t('desk_contract_done')}</button>
      </div>
    </div>
  )
}
